/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/icon_green_good.svg";
import __asset_1 from "@res/icons/icon_gray_neutral.svg";
import __asset_2 from "@res/icons/icon_red_bad.svg";
import __asset_3 from "@res/icons/Arrowhead_light_left.svg";
import __asset_4 from "@res/icons/Arrowhead_dark_left.svg";
import __asset_5 from "@res/icons/Arrowhead_light_right.svg";
import __asset_6 from "@res/icons/Arrowhead_dark_right.svg";
import * as React from "react";
import {createRef} from "react";
import * as emotion from "emotion";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {COLORS} from "@res/colors";
import {OnPatientDataUpdated, PatientsDataModule} from "@modules/PatientDataModule";
import {DB_PatientData, PsychologicalMemoryNames, TableKeys} from "@app/app-shared/patient-data";
import {GraphUtil} from "./GraphUtil";
import {icon_heart, icon_thumb_down, icon_thumb_side_gray, icon_thumb_up_green, icon_tick_gray, icon_tick_green} from "../patients/PatientTab";
import {DateTimesUtil} from "../patients/DateTimesUtil";
import {DB_Patient} from "@app/app-shared/patient";
import {NoData} from "./NoData";
import {BadImplementationException, currentTimeMillies, Day, deepClone, Week} from "@intuitionrobotics/ts-common";
import {
    checkinElements,
    ENERGY_LEVELS_CONFIG_KEY,
    GENERAL_WELL_BEING_CONFIG_KEY,
    Key_After_Breakfast,
    Key_After_Dinner,
    Key_After_Lunch,
    Key_Before_Breakfast,
    Key_Before_Dinner,
    Key_Before_Lunch,
    MeasurementRange,
    PAIN_LEVELS_CONFIG_KEY,
    PHYSICAL_EXERCISE_CONFIG_KEY,
    STRESS_REDUCTION_CONFIG_KEY
} from "@app/app-shared/care_config";
import {FONTS} from "@res/fonts";
import {OnTimeFilterChanged, UserPreferencesModule} from "@modules/UserPreferencesModule";
import {Card} from "../../widget/Card";
import {CardHeader} from "../../widget/CardHeader";
import {margin_rl_lg, margin_t_lg} from "@styles/styles";
import {GoalsConfigModule, OnCareConfigEntryUpdated} from "@modules/GoalsConfigModule";
import ContinuousTableGraph, {TableData} from "../../../_to_remove/ContinuousTableGraph";
import {SelfReportGraphWrapper} from "./SelfReportGraphWrapper";
import {PsychologicalTableGraph} from "./PsychologicalTableGraph";
import {Badge} from "../../widget/Badge";
import PainGraph, {PainData} from "./PainGraph";
import dayjs from "../../../utils/dayjs";
import {MedicationReminderPanel} from "../reminders/MedicationReminderPanel";
import {VGap} from "../../widget/VGap";
import {UICareGoal} from "@app/app-shared/api";

export const graphScroll = emotion.css`
 ::-webkit-scrollbar-track
 {
   background-color: #f6f6f9;
 };

::-webkit-scrollbar {
  height: 10px;
};

::-webkit-scrollbar-thumb {
  background: ${COLORS.grays_2};
  border-radius: 6px
};

::-webkit-scrollbar-thumb:hover {
  background: #b7bbc3;
}
`;

export const newPain = emotion.css(
    {
        height: 7,
        width: 7,
        backgroundColor: COLORS.pain_graph_yellow,
        borderRadius: '100%',
        transform: 'translate(-50%, -50%) !important'
    }
);

export const graphBodyStyle = `ll_h_s ${margin_rl_lg} ${margin_t_lg}`;
export const icon_green_happy = __asset_0;
export const icon_gray_neutral = __asset_1;
export const icon_red_sad = __asset_2;


type Props = {
    patient: DB_Patient
}

type GraphPanelWrapperState = {
    number_of_days: number
    graphs_day_width: { [k: string]: number }
    endDate: number
}

const MIN_DAY_WIDTH = 70;
const MIN_DAY_GT_WIDTH = 216;
const graphsDayWidthDefault: { [k: string]: number } = {
    bp: MIN_DAY_WIDTH,
    med: MIN_DAY_WIDTH,
    sr: MIN_DAY_WIDTH,
    ca: MIN_DAY_WIDTH,
    co: MIN_DAY_WIDTH,
    gt: MIN_DAY_GT_WIDTH
};

export class GraphPanelWrapper
    extends BaseComponent<Props, GraphPanelWrapperState>
    implements OnTimeFilterChanged, OnCareConfigEntryUpdated, OnPatientDataUpdated {

    private graphs: { [k: string]: React.RefObject<HTMLDivElement> } = {
        bp: createRef<HTMLDivElement>(),
        med: createRef<HTMLDivElement>(),
        sr: createRef<HTMLDivElement>(),
        ca: createRef<HTMLDivElement>(),
        co: createRef<HTMLDivElement>(),
        gt: createRef<HTMLDivElement>()
    };

    constructor(props: Props) {
        super(props);
        this.state = {
            number_of_days: UserPreferencesModule.getTimeFilterSelectedOption().days,
            graphs_day_width: graphsDayWidthDefault,
            endDate: dayjs().startOf("day").valueOf()
        };
    }

    __onCareConfigEntryUpdated() {
        this.forceUpdate();
    }

    __onPatientDataUpdated() {
        this.forceUpdate();
    }

    __onTimeFilterChanged = () => {
        this.setState(
            {
                number_of_days: UserPreferencesModule.getTimeFilterSelectedOption().days,
                graphs_day_width: graphsDayWidthDefault
            });
    };

    handleResize = () => {
        this.calcGraphWidth();
    };

    calcGraphWidth = () => {
        const graphs_day_width_ = Object.keys(this.graphs)
            .reduce((acc: { [p: string]: number }, k: string) => {
                const graph = this.graphs[k];
                const width = this.state.graphs_day_width[k];

                if (!graph || !graph.current || !width)
                    return acc;

                const diff = graph.current.offsetWidth - this.state.number_of_days * width;
                acc[k] = k === "gt" ? Math.max(MIN_DAY_GT_WIDTH, width + diff / 4) : Math.max(MIN_DAY_WIDTH,
                    width + diff / this.state.number_of_days);
                return acc;
            }, {});

        this.setState({
            graphs_day_width: {
                ...this.state.graphs_day_width,
                ...graphs_day_width_
            }
        });
    };

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize);
        this.calcGraphWidth();
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<GraphPanelWrapperState>, snapshot?: any) {
        if (prevState.number_of_days !== this.state.number_of_days) {
            this.calcGraphWidth();
        }
    }

    private generalCohortGraphPanel = (careConfigs?: UICareGoal[]) => {
        const data = PatientsDataModule.getData(this.props.patient?.unitId || "", TableKeys.CheckIn);
        const foundEnabled = careConfigs?.some(c => checkinElements.includes(c.name));
        if (!data && !foundEnabled)
            return null;
        return <Card>
            <CardHeader>
                <div className={"ll_h_c"}>
                    {GraphPanelWrapper.title("General tracking")}
                </div>
                {this.generalCohortLegend()}
            </CardHeader>
            {<>
                <div>
                </div>
                <div className={graphBodyStyle}>
                    <div className={"ll_v_s"} style={{
                        justifyContent: "space-around",
                        flex: "0 0 135px",
                        height: 190,
                        borderRight: `1px solid ${COLORS.neutral_gray_1}`
                    }}>
                        {FONTS.reg_14_secondary_dark("Energy level")}
                        {FONTS.reg_14_secondary_dark("General wellbeing")}
                    </div>
                    <div style={{width: 0, display: "flex", flex: 1}}>
                        <div ref={this.graphs.gt} className={graphScroll} style={{
                            width: "100%",
                            overflow: "auto",
                            position: "relative"
                            // display: 'flex',
                            // flexDirection: 'row-reverse'
                        }}>
                            {this.generalCohortGraph()}
                        </div>
                    </div>
                </div>
            </>}
        </Card>;
    };

    private painCohortGraphPanel = () => {
        const data = PatientsDataModule.getData(this.props.patient?.unitId || "", TableKeys.Pain);
        if (!data)
            return null;
        return <Card>
            <CardHeader>
                <div className={"ll_h_c"}>
                    {GraphPanelWrapper.title("Pain tracking")}
                </div>
                {this.renderScrollBetweenDates()}
                {this.painCohortLegend()}
            </CardHeader>
            {
                <div className={graphBodyStyle}>
                    <div className={"ll_v_s"} style={{
                        justifyContent: "space-around",
                        flex: "0 0 30px",
                        height: 290,
                        borderRight: `1px solid ${COLORS.neutral_gray_1}`
                    }}>
                        {FONTS.reg_14_secondary_dark("10")}
                        {FONTS.reg_14_secondary_dark("9")}
                        {FONTS.reg_14_secondary_dark("8")}
                        {FONTS.reg_14_secondary_dark("7")}
                        {FONTS.reg_14_secondary_dark("6")}
                        {FONTS.reg_14_secondary_dark("5")}
                        {FONTS.reg_14_secondary_dark("4")}
                        {FONTS.reg_14_secondary_dark("3")}
                        {FONTS.reg_14_secondary_dark("2")}
                        {FONTS.reg_14_secondary_dark("1")}
                    </div>
                    <div style={{width: 0, display: "flex", flex: 1}}>
                        <div ref={this.graphs.gt} style={{
                            width: "100%",
                            overflow: "visible",
                            position: "relative"
                        }}>
                            {this.painCohortGraph()}
                        </div>
                    </div>
                </div>
            }
        </Card>;
    };


    resolveCohortY = (label: string) => {
        let y = 0;
        switch (label) {
            case ENERGY_LEVELS_CONFIG_KEY:
                y = 1;
                break;
            case GENERAL_WELL_BEING_CONFIG_KEY:
                y = 0;
                break;
            default:
                throw new BadImplementationException("there are only two options for labels");
        }
        return y;
    };

    private renderScrollBetweenDates() {
        this.state.number_of_days
        const date = `${dayjs(this.state.endDate - 2 * Week + Day).format("MMM DD")} - ${dayjs(this.state.endDate).format("MMM DD")}`;
        const isForwardDisabled = this.state.endDate + 2 * Week > currentTimeMillies();
        const d = dayjs();
        d.subtract(183, "days")
        const isBackDisabled = this.state.endDate - 2 * Week <= d.toDate().getTime();
        return <div style={{display: "flex", flexDirection: "row"}}>
            <button style={{marginLeft: 40, backgroundColor: "white", border: "white", display: "flex", alignSelf: "center"}} disabled={isBackDisabled}
                    onClick={() => this.setState({endDate: this.state.endDate - 2 * Week})}>
                {isBackDisabled ? <img src={__asset_3}/> : <img src={__asset_4}/>}
            </button>


            {FONTS.reg_14(date, {marginLeft: 16, marginRight: 16, alignSelf: "center", display: "flex"})}

            <button style={{backgroundColor: "white", border: "white", display: "flex", alignSelf: "center"}} disabled={isForwardDisabled}
                    onClick={() => this.setState({endDate: this.state.endDate + 2 * Week})}>
                {isForwardDisabled ? <img src={__asset_5}/> : <img src={__asset_6}/>}
            </button>
        </div>;
    }

    private generalCohortLegend = () => {
        return GraphUtil.renderGraphLegend(
            [
                [
                    <img alt={"icon_thumb_up_green"} src={icon_thumb_up_green} height={18} width={18}/>,
                    FONTS.reg_11_secondary_dark("Positive")
                ],
                [
                    <img alt={"icon_thumb_side_gray"} src={icon_thumb_side_gray} height={18} width={18}/>,
                    FONTS.reg_11_secondary_dark("Neutral")
                ],
                [
                    <img alt={"icon_thumb_down"} src={icon_thumb_down} height={18} width={18}/>,
                    FONTS.reg_11_secondary_dark("Negative")
                ]
            ]);
    };

    private painCohortLegend = () => {
        return GraphUtil.renderGraphLegend(
            [
                [
                    <Badge show={true} badge={<div className={newPain}/>}/>,
                    FONTS.reg_11_secondary_dark("New pain", {marginLeft: 5})
                ]
            ]);
    };

    convertGeneralCohortData = (): TableData[] => {
        let patientData: DB_PatientData[] = [];
        checkinElements.forEach(_label => {
            if (_label === PAIN_LEVELS_CONFIG_KEY)
                return;
            const data = PatientsDataModule.getCondensedMap(TableKeys.CheckIn, this.props.patient?.unitId || "", 7, _label);
            patientData = patientData.concat(data || []);
        });
        const checkInTableData: TableData[] = [];
        patientData.forEach(_moodData => {
            if (_moodData.label === PAIN_LEVELS_CONFIG_KEY)
                return;

            else if (_moodData.value !== 0) {
                checkInTableData.push({
                    label: _moodData.label,
                    data: {x: _moodData.timestamp, y: this.resolveCohortY(_moodData.label), _id: _moodData._id},
                    icon: GraphUtil.resolveIconOnScale(_moodData.value, icon_thumb_up_green, icon_thumb_side_gray, icon_thumb_down,
                        _moodData.extraData?.details,
                        false,
                        this.getDefaultDescriptionsForMoodData(_moodData.label))
                });
            }
        });
        return checkInTableData;
    };

    getDefaultDescriptionsForMoodData = (label: string): string[] => {
        switch (label) {
            case ENERGY_LEVELS_CONFIG_KEY:
                return ["Low", "", "High"];
            case GENERAL_WELL_BEING_CONFIG_KEY:
                return ["Worse", "The same", "Better"];
            default:
                return [];
        }
    };

    convertPainData = (): PainData[] => {
        const painData: DB_PatientData[] = (PatientsDataModule.getData(this.props.patient?.unitId || "", TableKeys.Pain)?.data || [])
            .filter(x => x.extraData.painLevel || x.extraData.painLevel === 0);
        return this.getLastDataOfEachDayOfEachBodyPart(painData)
            .map(painReport => {
                return {
                    id: painReport._id,
                    timestamp: painReport.timestamp,
                    bodyPart: painReport.extraData.bodyPart,
                    level: painReport.extraData.painLevel,
                    feel: painReport.extraData.feelPain
                };
            })
            .filter(painDatum => {
                return painDatum.level >= 0 && painDatum.level <= 10 &&
                    dayjs(painDatum.timestamp).isAfter(dayjs(this.state.endDate).subtract(14, "day")) &&
                    dayjs(painDatum.timestamp).isSameOrBefore(dayjs(this.state.endDate).endOf("day"));
            })
            .sort((a, b) => a.timestamp - b.timestamp);
    };

    getLastDataOfEachDayOfEachBodyPart = (painData: DB_PatientData[]): DB_PatientData[] => {
        let newData: DB_PatientData[] = [];
        new Set(painData.map(x => x.extraData.bodyPart)).forEach(bodyPart => {
            if (!bodyPart)
                return;

            // iterate for each body part and get the last instance of each day
            const lastOfEachDay: DB_PatientData[] = []
            painData.filter(x => x.extraData.bodyPart === bodyPart).forEach(painDatum => {
                const indexOfExisting = lastOfEachDay.findIndex(newDatum => newDatum.date === painDatum.date)
                // if element not found then add the new one
                if (!indexOfExisting || indexOfExisting < 0) {
                    lastOfEachDay.push(painDatum);
                    return;
                }

                // else check if the new one is after the existing one
                if (lastOfEachDay[indexOfExisting].timestamp < painDatum.timestamp) {
                    // replace existing element with new one
                    lastOfEachDay[indexOfExisting] = painDatum;
                    return;
                }
            });
            newData = newData.concat(lastOfEachDay);
        });

        // clone elements and set timestamps to start of day
        newData = deepClone(newData.flat(1));
        newData.forEach(x => x.timestamp = dayjs.tz(x.timestamp, this.props.patient?.timezone).startOf("day").valueOf());
        return newData;
    };

    private generalCohortGraph = () => {
        const fixedNumberOfWeeks = 4;
        // const width = this.state.graphs_day_width.gt * fixedNumberOfWeeks;
        return <ContinuousTableGraph
            sizing={{height: 250}}
            frequency={Day * 7}
            frames={{startFrame: 0, endFrame: 1}}
            data={this.convertGeneralCohortData()}
            viewBox={fixedNumberOfWeeks}
            timeframe={fixedNumberOfWeeks}/>;
    };

    private painCohortGraph = () => {
        return <PainGraph
            data={this.convertPainData()}
            frequency={Day}
            height={350}
            frames={{startFrame: 1, endFrame: 10}}
            viewBox={14}
            endDate={dayjs(this.state.endDate).endOf("day").valueOf()}
        />;
    };

    SelfReportsLegend = () => {
        return GraphUtil.renderGraphLegend(
            [
                [
                    <img alt={"icon_thumb_up_green"} src={icon_thumb_up_green} height={18} width={18}/>,
                    FONTS.reg_11_secondary_dark("Good")
                ],
                [
                    <img alt={"icon_thumb_side_gray"} src={icon_thumb_side_gray} height={18} width={18}/>,
                    FONTS.reg_11_secondary_dark("Fine")
                ],
                [
                    <img alt={"icon_thumb_down"} src={icon_thumb_down} height={18} width={18}/>,
                    FONTS.reg_11_secondary_dark("Bad")
                ]
            ]);
    };

    convertMoodData = (): TableData[] => {
        const moodData = PatientsDataModule.getData(this.props.patient?.unitId || "", TableKeys.Mood)?.data || [];
        const sleepData = PatientsDataModule.getData(this.props.patient?.unitId || "", TableKeys.Sleep)?.data || [];
        const moodTableData: TableData[] = [];
        const sleepTableData: TableData[] = [];
        moodData.forEach(_moodData => {
            if (_moodData.value !== 0)
                moodTableData.push({
                    label: "mood",
                    data: {x: _moodData.timestamp, y: 1, _id: _moodData._id},
                    icon: GraphUtil.resolveIconOnScale(_moodData.value, icon_thumb_up_green, icon_thumb_side_gray, icon_thumb_down,
                        _moodData.extraData?.description)
                });
        });
        sleepData.map(_sleepData => {
            if (_sleepData.value !== 0)
                sleepTableData.push(
                    {
                        label: "sleep",
                        data: {x: _sleepData.timestamp, y: 0, _id: _sleepData._id},
                        icon: GraphUtil.resolveIconOnScale(_sleepData.value, icon_thumb_up_green, icon_thumb_side_gray, icon_thumb_down, _sleepData.extraData?.description)
                    });
        });
        return moodTableData.concat(sleepTableData);
    };


    private selfReportsGraph() {
        const data = this.convertMoodData();
        return data ? <ContinuousTableGraph
            sizing={{height: 250, width: this.state.graphs_day_width.sr * this.state.number_of_days}}
            frequency={86400000}
            frames={{startFrame: 0, endFrame: 1}}
            data={data}
            viewBox={this.state.number_of_days}
            timeframe={90}/> : <NoData/>;
    }

    private selfReportsWrapper = () => {
        return <Card>
            <CardHeader>
                {GraphPanelWrapper.title("Self Reports")}
                {this.SelfReportsLegend()}
            </CardHeader>
            {PatientsDataModule.getData(this.props.patient?.unitId || "", TableKeys.Mood) ?
                <div className={graphBodyStyle}>
                    <div className={"ll_v_s"} style={{
                        justifyContent: "space-around",
                        flex: "0 0 75px",
                        height: 190,
                        borderRight: `1px solid ${COLORS.neutral_gray_1}`
                    }}>
                        {FONTS.reg_14_secondary_dark("Feeling")}
                        {FONTS.reg_14_secondary_dark("Sleep")}
                    </div>
                    <div style={{width: 0, display: "flex", flex: 1}}>
                        <div ref={this.graphs.sr} className={graphScroll} style={{
                            width: "100%",
                            overflow: "auto",
                            position: "relative",
                            display: "flex",
                            flexDirection: "row-reverse"
                        }}>
                            {this.selfReportsGraph()}
                        </div>
                    </div>
                </div> : <NoData/>}
        </Card>;
    };


    careActivitiesFrequency = (physicalExerciseConfigEnabled: boolean, stressReductionConfigEnabled: boolean):
        { stress?: string, exercise?: string } | undefined => {
        if (!physicalExerciseConfigEnabled && !stressReductionConfigEnabled)
            return;

        let exerciseFrequency;
        let stressFrequency;
        if (physicalExerciseConfigEnabled) {
            exerciseFrequency = this.props.patient && PatientsDataModule.getFrequency(PHYSICAL_EXERCISE_CONFIG_KEY, this.props.patient.unitId);
        }

        if (stressReductionConfigEnabled) {
            stressFrequency = this.props.patient && PatientsDataModule.getFrequency(STRESS_REDUCTION_CONFIG_KEY, this.props.patient.unitId);
        }

        if (!exerciseFrequency && !stressFrequency)
            return;

        return {
            stress: stressFrequency && DateTimesUtil.frequencyPresentation(stressFrequency),
            exercise: exerciseFrequency && DateTimesUtil.frequencyPresentation(exerciseFrequency)
        };
    };

    convertStressData = (physicalExerciseConfigEnabled: boolean, stressReductionConfigEnabled: boolean): TableData[] => {
        // let currentExerciseAccomplishedDay: Moment;
        // let currentStressAccomplishedDay: Moment;

        const data = [];

        if (physicalExerciseConfigEnabled)
            data.push(...(PatientsDataModule.getData(this.props.patient?.unitId || "", TableKeys.Exercise)?.data || []))

        if (stressReductionConfigEnabled)
            data.push(...(PatientsDataModule.getData(this.props.patient?.unitId || "", TableKeys.Stress)?.data || []));

        return data.reduce((carry: TableData[], _data) => {
            if (!["exercise",
                "stress"].includes(_data.label)) {
                return carry;
            }

            const isExerciseData = _data.label === "exercise";

            // const currentAccomplishedDay: Moment = isExerciseData ? currentExerciseAccomplishedDay : currentStressAccomplishedDay;


            // const dataDay = moment(_data.timestamp).startOf("day");

            // if (currentAccomplishedDay && currentAccomplishedDay.startOf("day").isSame(dataDay))
            // 	return carry;

            const isAccomplished = _data.value >= 2;
            // if (isAccomplished) {
            // 	if (!currentAccomplishedDay || currentAccomplishedDay.startOf("day").isAfter(dataDay)) {
            //
            // 		if (isExerciseData)
            // 			currentExerciseAccomplishedDay = dataDay;
            // 		else
            // 			currentStressAccomplishedDay = dataDay;
            // 	}
            // }

            carry.push(
                {
                    label: _data.label, data: {x: _data.timestamp, y: isExerciseData ? 0 : 1, _id: _data._id}, icon:
                        isAccomplished ?
                            <img alt={"icon_tick_green"} src={icon_tick_green} height={28} width={28}
                                 style={{display: "block", margin: "auto"}}/>
                            : <img alt={"icon_tick_gray"} src={icon_tick_gray} height={28} width={28}
                                   style={{display: "block", margin: "auto"}}/>
                });

            return carry;
        }, []);
    }


    CareActivitiesLegend = () => {
        return GraphUtil.renderGraphLegend(
            [
                [
                    <img alt={"icon_tick_green"} src={icon_tick_green} height={18} width={18}/>,
                    FONTS.reg_11_secondary_dark("Completed")
                ] //Hide incomplete for the MVP
                ,
                [
                    <img alt={'icon_tick_gray'} src={icon_tick_gray} height={18} width={18}/>,
                    FONTS.reg_11_secondary_dark('Incomplete')
                ]
            ]);
    };

    private careActivitiesGraph(physicalExerciseConfigEnabled: boolean, stressReductionConfigEnabled: boolean) {
        const counter = [physicalExerciseConfigEnabled,
            stressReductionConfigEnabled].filter((o) => o).length;
        const data = this.convertStressData(physicalExerciseConfigEnabled, stressReductionConfigEnabled);
        return !data.length ? <NoData/> : <ContinuousTableGraph
            sizing={{height: 60 + 96, width: this.state.graphs_day_width.ca * this.state.number_of_days}}
            frequency={86400000}
            frames={{startFrame: 0, endFrame: counter - 1}}
            data={data}
            viewBox={this.state.number_of_days}
            timeframe={90}/>;
    }

    private careActivitiesWrapper = () => {
        // Don't display the panel, if both physical exercise and stress reductions configs are disabled.
        const physicalExerciseConfigEnabled = true; // (hidden for now, the plan is not active as part of the MVP) // !!(careConfig?.config[PHYSICAL_EXERCISE_CONFIG_KEY]?.enabled);
        const stressReductionConfigEnabled = true; // (display independently of any config, the plan is not active as part of the MVP) // !!(careConfig?.config[STRESS_REDUCTION_CONFIG_KEY]?.enabled);
        if (!physicalExerciseConfigEnabled && !stressReductionConfigEnabled)
            return null;

        return <Card>
            <CardHeader>
                {GraphPanelWrapper.title("Care activities")}
                {this.CareActivitiesLegend()}
            </CardHeader>
            {!PatientsDataModule.getData(this.props.patient?.unitId || "", TableKeys.Stress) || !this.convertStressData(physicalExerciseConfigEnabled,
                stressReductionConfigEnabled).length ?
                <NoData/> :
                <div className={graphBodyStyle} style={{position: "relative"}}>
                    <div className={"ll_v_s"} style={{
                        justifyContent: "space-around",
                        flex: "0 0 80px",
                        height: 96,
                        borderRight: `1px solid ${COLORS.neutral_gray_1}`
                    }}>
                        {
                            stressReductionConfigEnabled && FONTS.reg_14_secondary_dark("Stress reduction")
                        }
                        {
                            physicalExerciseConfigEnabled && FONTS.reg_14_secondary_dark("Physical exercise")
                        }
                    </div>
                    <div style={{width: 0, display: "flex", flex: 1, position: "relative"}}>
                        <div ref={this.graphs.ca} className={graphScroll} style={{
                            width: "100%", // '89%', (set previous value once frequency is displayed)
                            overflow: "auto",
                            position: "relative",
                            display: "flex",
                            flexDirection: "row-reverse"
                        }}>
                            {this.careActivitiesGraph(physicalExerciseConfigEnabled, stressReductionConfigEnabled)}
                        </div>
                    </div>
                    <div className={"ll_v_s"} style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        justifyContent: "space-between",
                        width: "10%",
                        height: 190
                    }}>
                        {/* not relevant for the MVP since those activities are not going to be configured in the care center */}
                        {/*this.renderExtraData(physicalExerciseConfigEnabled, stressReductionConfigEnabled)*/}
                    </div>
                </div>}
        </Card>;
    };

    private renderPHQTable() {
        return <PsychologicalTableGraph
            patient={this.props.patient}
            type={PsychologicalMemoryNames.Depression}
        />;
    }

    private renderAnxietyTable() {
        return <PsychologicalTableGraph
            patient={this.props.patient}
            type={PsychologicalMemoryNames.Anxiety}
        />;
    }

    render() {
        const careConfigs = GoalsConfigModule.getByUnitId(this.props.patient.unitId);
        const patient = this.props.patient;
        const careConfigsMap = careConfigs.reduce((acc: Map<string, MeasurementRange>, c) => acc.set(c.name, c.ranges), new Map());
        return <>
            <SelfReportGraphWrapper
                patient={patient}
                settings={[{
                    label: "weight",
                    text: "weight",
                    color: COLORS.sap_green,
                    validRanges: careConfigsMap.get("weight")
                }]}
                title={"Weight"}
                range={{maxRange: 400, minRange: 0}}
                measurement={"LBS"}
                lines={true}
            />
            <SelfReportGraphWrapper
                patient={patient}
                settings={[
                    {
                        label: Key_Before_Breakfast,
                        text: "Before breakfast",
                        color: COLORS.sap_green,
                        validRanges: careConfigsMap.get("blood_glucose")
                    },
                    {
                        label: Key_After_Breakfast,
                        text: "After breakfast",
                        color: COLORS.sap_green,
                        validRanges: careConfigsMap.get("blood_glucose")
                    },
                    {
                        label: Key_Before_Lunch,
                        text: "Before lunch",
                        color: COLORS.sap_green,
                        validRanges: careConfigsMap.get("blood_glucose")
                    },
                    {
                        label: Key_After_Lunch,
                        text: "After lunch",
                        color: COLORS.sap_green,
                        validRanges: careConfigsMap.get("blood_glucose")
                    },
                    {
                        label: Key_Before_Dinner,
                        text: "Before dinner",
                        color: COLORS.sap_green,
                        validRanges: careConfigsMap.get("blood_glucose")
                    },
                    {
                        label: Key_After_Dinner,
                        text: "After dinner",
                        color: COLORS.sap_green,
                        validRanges: careConfigsMap.get("blood_glucose")
                    },
                    {
                        label: "postprandial",
                        text: "PPGR",
                        color: COLORS.sap_green,
                        validRanges: careConfigsMap.get("blood_glucose")
                    },
                    {
                        label: "fasted",
                        text: "Fasting glucose",
                        color: COLORS.sap_green,
                        validRanges: careConfigsMap.get("blood_glucose")
                    }]}
                title={"Blood glucose"}
                range={{maxRange: 350, minRange: 50}}
                measurement={"mg/dL"}
            />
            <SelfReportGraphWrapper
                patient={patient}
                settings={[
                    {
                        label: "systolic",
                        text: "Systolic",
                        color: COLORS.primary_dark_blue,
                        validRanges: careConfigsMap.get("blood_pressure")
                    },
                    {
                        label: "diastolic",
                        text: "Diastolic",
                        color: "#77bdbd",
                        validRanges: careConfigsMap.get("blood_pressure")
                    },
                    {
                        label: "heart_rate",
                        text: "Pulse",
                        color: "#de3a8b",
                        icon: icon_heart,
                        validRanges: careConfigsMap.get("heart_rate")
                    }]}
                title={"Blood pressure"}
                range={{maxRange: 220, minRange: 30}}
                measurement={"mmHg"}
                inGraphLegend={true}
                isBloodPressure={true}
                yTickSpacing={20}
            />
            <SelfReportGraphWrapper
                patient={patient}
                settings={[{
                    label: "blood_oxygen",
                    text: "Oxygen Saturation",
                    color: COLORS.sap_green,
                    validRanges: careConfigsMap.get("blood_oxygen")
                }]}
                title={"Blood oxygen saturation"}
                range={{maxRange: 100, minRange: 0}}
                measurement={"SpO2"}
                valuesSuffix={"%"}
                lines={true}
                yTickSpacing={10}
            />
            {this.painCohortGraphPanel()}
            <VGap v={16}/>
            <MedicationReminderPanel patient={this.props.patient}/>
            {this.generalCohortGraphPanel(careConfigs)}
            {this.selfReportsWrapper()}
            {this.careActivitiesWrapper()}
            {this.renderPHQTable()}
            {this.renderAnxietyTable()}
        </>;
    }

    public static title(label: string) {
        return FONTS.medium_18_graysdark(label, {whiteSpace: "nowrap"});
    }

}
